/**
 * EN-1492: edit rows for the keep-or-discard modal and section regeneration, through apiFetch (moved out of api.ts).
 */
import * as Sentry from "@sentry/react";
import { apiFetch } from "@/common/lib/api";
import { classifyNetworkError } from "@/common/lib/sentryUtils";

export type EditRowKind = "text" | "list" | "title" | "extra";
export type EditRowTag = "reverted_to_normal" | "cleared" | "added" | "renamed";
export type EditRow = {
    /** Section id, field id, the title sentinel, the document text sentinel, or the extra item's own id. */
    id: string;
    kind: EditRowKind;
    tag?: EditRowTag;
    /** Parent section for a nested field or extra field. */
    sectionId?: string;
    sectionName?: string;
    name: string;
    nested: boolean;
    /** What HappyDoc wrote; null for extra content. */
    baseline: string | string[] | null;
    /** What the vet has now; a blank string for cleared. */
    current: string | string[] | null;
};
/** O8: the one row of a document whose text is a plain string (a legacy non-JSON type), matching the server. */
export const DOCUMENT_TEXT_ROW_ID = "__document_text__";
export type EditChoice = {
    documentTypeId: string;
    patientName?: string | null;
    sourceChatCompletionId: string;
    keepRowIds: string[];
};
export type EditRowsDocument = {
    documentTypeId: string;
    patientName: string | null;
    sourceChatCompletionId: string;
    header: string;
    inputs: { transcriptSectionIds: string[]; documentAssetIds: string[] };
    rows: EditRow[];
};

/** The newest completed version of a type (per pet), whether or not it has edits. */
export type LatestVersion = {
    documentTypeId: string;
    patientName: string | null;
    chatCompletionId: string;
    header: string;
};

/**
 * Report a failed call without the raw ky error, whose message and request carry the URL query
 * (pet name, ids). Only the status and the route path go to Sentry.
 */
const reportFailure = (name: string, path: string, error: unknown) => {
    const status = statusOf(error);
    Sentry.captureException(new Error(`${name} failed: ${status ?? "network"} /${path}`), {
        tags: { errorCategory: classifyNetworkError(error) },
    });
};

const statusOf = (error: unknown): number | undefined =>
    (error as { response?: { status?: number } })?.response?.status;

/**
 * Rows for the keep-or-discard modal, plus the latest completed version of each type. A 404 from an API
 * without the route resolves to no documents and no versions (`[]`). `latestVersions` is undefined only when a
 * response leaves it out, which no server with the route does.
 */
export const fetchEditRowsWithLatest = async (p: {
    patientRecordId: string;
    documentTypeIds: string[];
    patientNames?: string[];
    /** Build the rows from this version, not the latest (a regenerate of an older tab). */
    chatCompletionId?: string;
}): Promise<{ documents: EditRowsDocument[]; latestVersions?: LatestVersion[] }> => {
    try {
        const res = await apiFetch<{ documents: EditRowsDocument[]; latestVersions?: LatestVersion[] }>(
            "generate/editRows",
            { method: "POST", json: p, silent: true },
        );
        return { documents: res?.documents ?? [], latestVersions: res?.latestVersions };
    } catch (error) {
        // A 404 means the API predates EN-1492 (or was rolled back): no documents, so generation behaves as before.
        if (statusOf(error) === 404) return { documents: [], latestVersions: [] };
        // silent: the callers toast; report here without the raw ky error (URL, ids).
        reportFailure("fetchEditRows", "generate/editRows", error);
        throw error;
    }
};

/** Rows for the keep-or-discard modal. A 404 from an API without the route resolves to no documents. */
export const fetchEditRows = async (p: Parameters<typeof fetchEditRowsWithLatest>[0]): Promise<EditRowsDocument[]> =>
    (await fetchEditRowsWithLatest(p)).documents;

/**
 * Regeneration runs as long as the model does, so no client timeout (R10). `silent`: the caller
 * shows its own toast, so apiFetch skips Sentry and the network toast. Never throws.
 */
export const regenerateSection = async (
    chatId: string,
    sectionId: string,
    opts: { preserveEdits: boolean; keepRowIds?: string[]; provider?: string },
): Promise<{ error: unknown | null }> => {
    try {
        await apiFetch("generate/regenerate", {
            method: "POST",
            json: { chatId, sectionId, ...opts },
            timeout: false,
            silent: true,
        });
        return { error: null };
    } catch (error) {
        reportFailure("regenerateSection", "generate/regenerate", error);
        return { error };
    }
};

/** A document a Stop & Generate dialog is asking about: its generation waits for the answer (D20, D21). */
export type PendingEditChoice = { documentTypeId: string; patientName: string | null };

/**
 * D20: tells the API that the vet is choosing what to keep for these documents, so the generation the recording
 * starts waits for the answer (bounded, D21). Resolves false on any failure, including a 404 from an API without
 * the route: generation then keeps all (D23). Never throws.
 */
export const registerPendingEditChoices = async (p: {
    patientRecordId: string;
    documents: PendingEditChoice[];
}): Promise<boolean> => {
    try {
        await apiFetch("generate/editChoices/pending", { method: "POST", json: p, silent: true });
        return true;
    } catch (error) {
        if (statusOf(error) !== 404) reportFailure("registerPendingEditChoices", "generate/editChoices/pending", error);
        return false;
    }
};

/**
 * Releases pending entries that have nothing to ask (no edits, or the check failed), so their generations keep all at
 * once instead of waiting for an answer that will never come (D21). Resolves false on any failure, including a 404
 * from an older API; the wait is bounded either way. Never throws.
 */
export const releaseEditChoices = async (p: {
    patientRecordId: string;
    documents: PendingEditChoice[];
}): Promise<boolean> => {
    try {
        await apiFetch("generate/editChoices/release", { method: "POST", json: p, silent: true });
        return true;
    } catch (error) {
        if (statusOf(error) !== 404) reportFailure("releaseEditChoices", "generate/editChoices/release", error);
        return false;
    }
};

/** Whether the answer reached each document; false means it was already written keeping all edits. */
export type AppliedEditChoice = { documentTypeId: string; patientName: string | null; applied: boolean };

/** D22: the Stop & Generate dialog's answer. Resolves null on any failure (generation keeps all). Never throws. */
export const answerEditChoices = async (p: {
    patientRecordId: string;
    editChoices: EditChoice[];
}): Promise<AppliedEditChoice[] | null> => {
    try {
        const res = await apiFetch<{ applied: AppliedEditChoice[] }>("generate/editChoices/answer", {
            method: "POST",
            json: p,
            silent: true,
        });
        return res?.applied ?? [];
    } catch (error) {
        if (statusOf(error) !== 404) reportFailure("answerEditChoices", "generate/editChoices/answer", error);
        return null;
    }
};
